// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import type { Label, LabelId, LabelTypeId } from '@evolvemep/api-ts';
import { Card, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EditableLabel } from 'components/Mantine/Labels/ManageLabelsModal/EditableLabel';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { useLabels } from 'hooks/typeCacheHooks/useLabels/useLabels';

type Props = {
  labelTypeId: LabelTypeId;
};

export const LabelsPanelContents = ({ labelTypeId }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const { labels, deleteLabel } = useLabels(labelTypeId);
  const [labelToEdit, setLabelToEdit] = useState<LabelId | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Label | null>(null);

  const searchLower = searchValue.toLowerCase();
  const filteredLabels = useMemo(
    () => labels.filter((label) => label.name.toLowerCase().includes(searchLower)),
    [labels, searchLower],
  );
  return (
    <>
      <TextInput
        placeholder="Search..."
        value={searchValue}
        onChange={(e) => setSearchValue(e.currentTarget.value)}
        w={300}
      />
      <Card>
        {filteredLabels.length === 0 && (
          <Card.Section>
            <Text c="dimmed" fz="sm" m="xs">
              {labels.length === 0 ? 'No labels.' : 'No matching labels.'}
            </Text>
          </Card.Section>
        )}
        {filteredLabels.map((label) => (
          <Card.Section key={label.labelId} withBorder>
            <EditableLabel
              label={label}
              labelTypeId={labelTypeId}
              editing={labelToEdit === label.labelId}
              editable={!labelToEdit}
              onEditLabel={(l) => setLabelToEdit(l.labelId)}
              onLabelUpdated={() => setLabelToEdit(null)}
              onCancel={() => setLabelToEdit(null)}
              onDeleteLabel={setConfirmDelete}
            />
          </Card.Section>
        ))}
      </Card>
      <VariableTrackingModal
        title="Delete label"
        size="sm"
        buttonColor="red"
        variable={confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async (deletingLabel) => {
          await deleteLabel(deletingLabel.labelId).then(() => {
            notifications.show({
              title: 'Label deleted',
              message: 'Label successfully deleted',
              color: 'green',
            });
          });
        }}
      >
        {(deletingLabel) => (
          <Text>
            Delete label <b>{deletingLabel.name}</b>?
          </Text>
        )}
      </VariableTrackingModal>
    </>
  );
};
